import React, { useState } from 'react';
import { C, S } from '../utils/styles';
import Icon from './Icon';

export default function EnablePortalAccessModal({ contact, clientName, onConfirm, onCancel }) {
  const [tempPassword, setTempPassword] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const handleConfirm = async () => {
    const result = await onConfirm();
    if (result.success) {
      setTempPassword(result.tempPassword);
    }
  };
  
  const handleCopyPassword = () => {
    if (tempPassword) {
      navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  if (tempPassword) {
    // Show success screen with password
    return (
      <div 
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
        }}
        onClick={onCancel}
      >
        <div 
          style={{
            background: C.card,
            borderRadius: 12,
            padding: '28px 32px',
            width: '90%',
            maxWidth: 500,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ 
              width: 48, 
              height: 48, 
              borderRadius: '50%', 
              background: C.greenLight, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <Icon name="checkmark-outline" style={{ fontSize: 28, color: C.green }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.text }}>
                Portal Access Enabled
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: C.textMid }}>
                {contact.firstName} {contact.lastName}
              </p>
            </div>
          </div>
          
          <div style={{ 
            background: C.orangeLight, 
            border: `1px solid ${C.orange}`,
            borderRadius: 8, 
            padding: '14px 16px',
            marginBottom: 20,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.orange, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
              Temporary Password
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12,
              background: 'white',
              padding: '12px 14px',
              borderRadius: 6,
            }}>
              <code style={{ 
                flex: 1,
                fontSize: 16, 
                fontWeight: 700, 
                color: C.text,
                fontFamily: 'monospace',
                letterSpacing: '0.5px',
              }}>
                {tempPassword}
              </code>
              <button
                onClick={handleCopyPassword}
                style={{
                  padding: '8px 14px',
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'white',
                  background: copied ? C.green : C.accent,
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'background 0.2s',
                }}
              >
                <Icon name={copied ? 'checkmark-outline' : 'copy-outline'} style={{ fontSize: 14 }} />
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          
          <div style={{ 
            fontSize: 13, 
            color: C.textMid, 
            lineHeight: 1.6,
            marginBottom: 20,
          }}>
            <p style={{ margin: '0 0 12px' }}>
              <strong style={{ color: C.text }}>⚠️ Share this password securely.</strong>
            </p>
            <p style={{ margin: 0 }}>
              {contact.firstName} will be prompted to set a new password on first login. 
              They can access the portal at the same login screen using their email: <strong style={{ color: C.text }}>{contact.email}</strong>
            </p>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={onCancel}
              style={{
                padding: '10px 20px',
                fontSize: 13,
                fontWeight: 700,
                color: 'white',
                background: C.accent,
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Show confirmation screen
  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={onCancel}
    >
      <div 
        style={{
          background: C.card,
          borderRadius: 12,
          padding: '28px 32px',
          width: '90%',
          maxWidth: 480,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: C.text }}>
          Enable Employer Portal Access
        </h3>
        
        <div style={{ 
          background: C.bg,
          borderRadius: 8,
          padding: '14px 16px',
          marginBottom: 20,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 12px', fontSize: 13 }}>
            <span style={{ color: C.textMid, fontWeight: 600 }}>Contact:</span>
            <span style={{ color: C.text, fontWeight: 600 }}>{contact.firstName} {contact.lastName}</span>
            
            <span style={{ color: C.textMid, fontWeight: 600 }}>Email:</span>
            <span style={{ color: C.text }}>{contact.email}</span>
            
            <span style={{ color: C.textMid, fontWeight: 600 }}>Company:</span>
            <span style={{ color: C.text }}>{clientName}</span>
          </div>
        </div>
        
        <div style={{ 
          fontSize: 13, 
          color: C.textMid, 
          lineHeight: 1.6,
          marginBottom: 20,
        }}>
          <p style={{ margin: '0 0 12px' }}>
            This will create a login for {contact.firstName} to access the Employer Dashboard for {clientName}. 
            They will see their company's plan details, employee benefits, and program information.
          </p>
          <p style={{ margin: '0 0 12px' }}>
            <strong style={{ color: C.text }}>They will NOT have access to:</strong>
          </p>
          <ul style={{ margin: '0 0 0 20px', paddingLeft: 0 }}>
            <li>HealthCues internal tools</li>
            <li>Other clients' information</li>
            <li>Admin settings</li>
          </ul>
        </div>
        
        <div style={{ 
          background: C.blueLight,
          border: `1px solid ${C.blue}`,
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Icon name="information-circle-outline" style={{ fontSize: 20, color: C.blue, marginTop: 2 }} />
            <div style={{ fontSize: 12, color: C.blue, lineHeight: 1.5 }}>
              A temporary password will be generated. Share it securely with {contact.firstName}. 
              They'll be prompted to change it on first login.
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 18px',
              fontSize: 13,
              fontWeight: 600,
              color: C.textMid,
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            style={{
              padding: '10px 18px',
              fontSize: 13,
              fontWeight: 700,
              color: 'white',
              background: C.accent,
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name="checkmark-outline" style={{ fontSize: 16 }} />
            Enable Access
          </button>
        </div>
      </div>
    </div>
  );
}
